import { FC, useEffect, useState } from 'react'
import { Box, Button, DialogActions, DialogTitle, Paper, Typography } from '@mui/material'
import { DataGridPremium, GridPaginationModel, useGridApiRef } from '@mui/x-data-grid-premium'
import JsonView from '@uiw/react-json-view'
import { SensorConfigurationRequest } from 'src/api'
import { getSensorConfigurationRequestColumns, SensorConfigurationRequestPayload } from 'src/components/columns/sensorConfigurationRequest'
import { DataLoad, StatefulDialog, TableToolbar } from 'src/components/ui'
import { DialogNames } from 'src/enums'
import { useSensorConfigurationRequestListRequest } from 'src/hooks/api'
import { useDialog } from 'src/hooks/ui'

interface SensorConfigurationHistoryProps {
  sensorUniqueId: string
}

const defaultPageSize = 10
const rowsPerPageOptions = [10, 25, 50, 100]

export const SensorConfigurationHistory: FC<SensorConfigurationHistoryProps> = ({ sensorUniqueId }) => {
  const apiRef = useGridApiRef()
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(defaultPageSize)
  const [payload, setPayload] = useState<SensorConfigurationRequestPayload | null>(null)
  const { openDialog, closeDialog } = useDialog(DialogNames.SensorConfigurationRequestPayload)

  const { data, error, isError, isFetching } = useSensorConfigurationRequestListRequest({
    params: { uniqueId: sensorUniqueId, limit: pageSize, offset: page * pageSize },
    options: { keepPreviousData: true }
  })

  // Keep the previous count while the next page is loading, otherwise the grid resets to page 1.
  const [rowCountState, setRowCountState] = useState<number>(data?.count || 0)
  useEffect(() => {
    setRowCountState((prev) => (data?.count !== undefined ? data.count : prev))
  }, [data?.count])

  const handleViewPayload = (nextPayload: SensorConfigurationRequestPayload) => {
    setPayload(nextPayload)
    openDialog()
  }

  const handleClosePayload = () => {
    closeDialog(() => setPayload(null))
  }

  const columns = getSensorConfigurationRequestColumns(handleViewPayload)

  const handleChangePaginationModel = (pagination: GridPaginationModel): void => {
    setPage(pagination.page)
    setPageSize(pagination.pageSize)
  }

  return (
    <>
      <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
        <Paper>
          <DataGridPremium<SensorConfigurationRequest>
            apiRef={apiRef}
            autoHeight
            pagination
            paginationMode="server"
            disableRowSelectionOnClick
            rows={data?.rows || []}
            columns={columns}
            rowCount={rowCountState}
            loading={isFetching}
            getRowId={(row) => row.id as number}
            paginationModel={{ page, pageSize }}
            pageSizeOptions={rowsPerPageOptions}
            onPaginationModelChange={handleChangePaginationModel}
            rowThreshold={2}
            columnThreshold={2}
            localeText={{
              noRowsLabel: 'No configuration requests yet',
              columnMenuSortAsc: 'Sort A-Z',
              columnMenuSortDesc: 'Sort Z-A'
            }}
            slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_configuration_history`} /> }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1,
                fontSize: '13px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.6)',
                overflow: 'visible'
              }
            }}
          />
        </Paper>
      </DataLoad>
      <StatefulDialog
        name={DialogNames.SensorConfigurationRequestPayload}
        onClose={() => setPayload(null)}
        DialogProps={{ fullWidth: true, maxWidth: 'md' }}
        title={<DialogTitle>{payload?.title || 'Details'}</DialogTitle>}
        actions={
          <DialogActions>
            <Button onClick={handleClosePayload}>Close</Button>
          </DialogActions>
        }
      >
        {payload?.value && Object.keys(payload.value).length > 0 ? (
          <Box sx={{ overflow: 'auto' }}>
            <JsonView value={payload.value} collapsed={2} />
          </Box>
        ) : (
          <Typography variant="body1">No data</Typography>
        )}
      </StatefulDialog>
    </>
  )
}
